/**
 * Gate del Vault (F3a). Sigue el molde de PartnershipsProtectedRoute pero con
 * PANTALLAS EXPLÍCITAS en vez de redirect — nunca una página vacía:
 *   sin 2SV de Google   → MfaRequiredScreen (variant vault)
 *   sin passphrase      → PassphraseSetupWizard
 *   todo ok             → <Outlet />
 *
 * `vault.getMyAccess` es privateProcedure y nunca throwea (política de roles y
 * visibilidad del repo).
 */
import { Outlet } from 'react-router-dom';
import { trpc } from '@/lib/trpc';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { MfaRequiredScreen } from '@/components/auth/MfaRequiredScreen';
import { PassphraseSetupWizard } from './PassphraseSetupWizard';
import { useVaultAccess } from '@/hooks/useVault';
import { subscribeMfaRequired } from '@/lib/vault-session';
import { useEffect } from 'react';

export function VaultProtectedRoute() {
  const utils = trpc.useUtils();
  const access = useVaultAccess();

  // Si una llamada del Vault contesta MFA_REQUIRED en medio de la sesión, se vuelve
  // a resolver el acceso y esta ruta muestra la pantalla de 2SV.
  useEffect(
    () =>
      subscribeMfaRequired(() => {
        void utils.vault.getMyAccess.invalidate();
        void utils.profile.getMfaStatus.invalidate();
      }),
    [utils]
  );

  // "Ya la activé": fuerza la consulta a la Directory API (salta el cache del
  // backend) y vuelve a resolver el acceso al Vault.
  const recheckMfa = async () => {
    await utils.client.profile.getMfaStatus.query({ refresh: true });
    await utils.vault.getMyAccess.invalidate();
    await utils.profile.getMfaStatus.invalidate();
  };

  if (access.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <WinclapLoader size={48} className="text-foreground/25" />
      </div>
    );
  }

  const data = access.data;
  if (!data || data.mfaEnrolled !== true) {
    return <MfaRequiredScreen variant="vault" enrolled={data?.mfaEnrolled ?? null} onRecheck={recheckMfa} rechecking={access.isFetching} />;
  }

  if (!data.hasPassphrase) {
    return <PassphraseSetupWizard onDone={() => utils.vault.getMyAccess.invalidate()} />;
  }

  return <Outlet />;
}
